import { AddIcon, WarningIcon } from "@chakra-ui/icons";
import {
	Box,
	Badge,
	Button,
	Icon,
	Switch,
	Image,
	Text,
	Flex,
	Alert,
	AlertIcon,
	AlertDescription,
	useToast,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { apiRequest, apiGetRequest } from "utils/api";

export interface IntegrationCardProps {
	controller: string;
	name: string;
	description: string;
	logo: string;
	connected: boolean;
}

export function IntegrationCard({
	name,
	description,
	logo,
	connected,
	controller,
}: IntegrationCardProps) {
	const [isConnected, setIsConnected] = useState(connected);
	const [isLoading, setIsLoading] = useState(false);
	const [connectionError, setConnectionError] = useState<string | null>(null);
	const [showForceDisconnect, setShowForceDisconnect] = useState(false);
	const toast = useToast();

	// Test Xero connection on load to detect authentication issues
	useEffect(() => {
		async function testXeroConnection() {
			if (name === "Xero" && isConnected) {
				try {
					// Try to fetch accounts to test if authentication works
					await apiGetRequest("/xero/accounts");
				} catch (error: any) {
					const errorMessage = error?.response?.data?.message || error?.message || "";
					
					if (errorMessage.includes("expired") || errorMessage.includes("revoked") || errorMessage.includes("invalid_grant")) {
						setConnectionError("Connection appears to be expired or invalid. You may need to force disconnect.");
						setShowForceDisconnect(true);
					}
				}
			}
		}

		testXeroConnection();
	}, [name, isConnected]);

	async function connect() {
		try {
			setIsLoading(true);
			setConnectionError(null);
			const response = await apiRequest(`/${controller}/connect`, {}, "post");
			console.log(response.url);
			window.location.href = response.url;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || error?.message || "Failed to connect";
			setConnectionError(errorMessage);
			toast({
				title: "Connection failed",
				description: errorMessage,
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setIsLoading(false);
		}
	}

	async function disconnect() {
		setIsLoading(true);
		setConnectionError(null);
		try {
			await apiRequest(`/${controller}/disconnect`, {}, "post");
			setIsConnected(false);
			setShowForceDisconnect(false);
			toast({
				title: "Successfully disconnected",
				description: `${name} has been disconnected from your account.`,
				status: "success",
				duration: 3000,
				isClosable: true,
			});
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || error?.message || "Failed to disconnect";
			
			if (errorMessage.includes("expired") || errorMessage.includes("revoked") || errorMessage.includes("invalid_grant")) {
				setConnectionError("Connection appears to be expired or invalid. You may need to force disconnect.");
				setShowForceDisconnect(true);
			} else {
				setConnectionError(errorMessage);
			}
			
			toast({
				title: "Disconnect failed",
				description: errorMessage,
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setIsLoading(false);
		}
	}

	async function forceDisconnect() {
		setIsLoading(true);
		setConnectionError(null);
		try {
			await apiRequest(`/${controller}/force-disconnect`, {}, "post");
			setIsConnected(false);
			setShowForceDisconnect(false);
			toast({
				title: "Force disconnect successful",
				description: `${name} has been forcefully disconnected. You can now reconnect.`,
				status: "success",
				duration: 3000,
				isClosable: true,
			});
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || error?.message || "Failed to force disconnect";
			setConnectionError(errorMessage);
			toast({
				title: "Force disconnect failed",
				description: errorMessage,
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setIsLoading(false);
		}
	}

	async function handleToggle(isConnected: boolean) {
		if (isConnected) {
			await disconnect();
		} else {
			setConnectionError(null);
			setShowForceDisconnect(false);
			await connect();
		}
	}

	return (
		<Box
			key={name}
			padding="24px"
			borderRadius="16px"
			border="1px solid #E2E2E2"
			display="flex"
			flexDirection="column"
			minHeight="220px"
			transition="all 0.2s"
			_hover={{
				boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
				borderColor: "#D1D1D1",
			}}
		>
			<Box flex="1">
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					mb="16px"
				>
					<Image
						src={logo}
						borderRadius="8px"
						boxSize="40px"
						alt={name}
						objectFit="contain"
					/>
					{isConnected && !connectionError && (
						<Badge
							background="#ECF9ED"
							color="green.500"
							fontSize="12px"
							fontWeight="600"
							px="8px"
							py="2px"
							borderRadius="full"
						>
							Connected
						</Badge>
					)}
					{isConnected && connectionError && (
						<Badge
							background="#FEF2F2"
							color="red.500"
							fontSize="12px"
							fontWeight="600"
							px="8px"
							py="2px"
							borderRadius="full"
						>
							Connection Error
						</Badge>
					)}
				</Box>
				<Text fontWeight="600" fontSize="16px" mb="8px">
					{name}
				</Text>
				<Text fontSize="14px" color="gray.600">
					{description}
				</Text>

				{connectionError && (
					<Alert status="warning" size="sm" mt="12px" borderRadius="8px">
						<AlertIcon boxSize="16px" />
						<AlertDescription fontSize="12px">
							{connectionError}
						</AlertDescription>
					</Alert>
				)}
			</Box>

			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mt="16px"
				pt="16px"
				borderTop="1px solid #E2E2E2"
			>
				<Flex gap={4} flexWrap="wrap">
					<Button
						isLoading={isLoading}
						variant={isConnected ? "link" : "outline"}
						size="sm"
						display="flex"
						gap="4px"
						onClick={() => handleToggle(isConnected)}
						color={isConnected ? "red.500" : "blue.500"}
					>
						{isConnected ? (
							"Disconnect"
						) : (
							<>
								<Icon as={AddIcon} />
								Connect
							</>
						)}
					</Button>
					{showForceDisconnect && name === "Xero" && (
						<Button
							isLoading={isLoading}
							variant="outline"
							size="sm"
							display="flex"
							gap="4px"
							onClick={forceDisconnect}
							colorScheme="red"
							leftIcon={<WarningIcon />}
						>
							Force Disconnect
						</Button>
					)}
					{isConnected && name === "Xero" && !showForceDisconnect && (
						<Button
							isDisabled={isLoading}
							variant="link"
							size="sm"
							onClick={() => handleToggle(false)}
						>
							Add another account
						</Button>
					)}
				</Flex>
				{isConnected && !connectionError && (
					<Box display="flex" alignItems="center" gap="8px">
						<Switch
							isDisabled={isLoading}
							size="sm"
							isChecked={isConnected}
							colorScheme="green"
							onChange={() => handleToggle(true)}
						/>
						<Text fontSize="14px" color="gray.600">
							Active
						</Text>
					</Box>
				)}
			</Box>
		</Box>
	);
}
